import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import { MBTI_GROUPS, clubInfo } from "@/lib/mbti";
import { Briefcase, Trash2, UserPlus, Save } from "lucide-react";

const ALL_MBTI = MBTI_GROUPS.flatMap((g) => (g.types || []).map((t) => t.code));

const empty = { email: "", name: "", department: "", job_title: "", phone: "", password: "" };

/**
 * Admin page for managing Staff members (read-only members of KW Lead with access to
 * trainings of their MBTI club but no competition / no Player record).
 * Admin sets the initial password directly here (same flow as /coaches); staff then /login.
 */
export default function StaffAdmin() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty);
  const [pending, setPending] = useState({}); // { [id]: { name?, department?, phone?, coach_mbti? } }

  const refresh = () => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "staff")));
  useEffect(() => { refresh(); }, []);

  const setField = (id, key, value) => {
    setPending((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: value } }));
  };

  const save = async (id) => {
    const patch = pending[id];
    if (!patch || Object.keys(patch).length === 0) { toast.info("Nada para guardar."); return; }
    try {
      await api.put(`/users/${id}`, patch);
      toast.success("Atualizado");
      setPending((p) => { const n = { ...p }; delete n[id]; return n; });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error("Email e nome são obrigatórios."); return; }
    if (!form.password || form.password.length < 6) { toast.error("Password obrigatória (mín. 6 caracteres)."); return; }
    try {
      await api.post("/users", {
        email: form.email.trim().toLowerCase(),
        name: form.name,
        password: form.password,
        role: "staff",
        department: form.department || null,
        job_title: form.job_title || null,
        phone: form.phone || null,
      });
      toast.success("Staff criado. Já pode entrar em /login com esta password.");
      setForm(empty);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remover ${name} do staff?`)) return;
    try { await api.delete(`/users/${id}`); toast.success("Removido"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async (id, name) => {
    const newPwd = window.prompt(`Nova password para ${name} (mín. 6 caracteres):`);
    if (!newPwd) return;
    if (newPwd.length < 6) { toast.error("Password deve ter pelo menos 6 caracteres."); return; }
    try { await api.put(`/users/${id}`, { password: newPwd }); toast.success("Password atualizada."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="staff-admin-page">
      <div>
        <div className="kw-overline">Administração</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">Staff KW Lead</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Membros do staff (marketing, operações, RH, etc.) com acesso à plataforma para verem o
          Dashboard, Rankings, Hall of Fame e Formações do seu perfil MBTI. <strong className="text-white">Não coopetem</strong> no
          ranking nem têm registo como consultores. Definem o próprio MBTI no 1.º login.
        </p>
      </div>

      <form onSubmit={add} className="kw-card p-5 grid md:grid-cols-6 lg:grid-cols-7 gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Nome</label>
          <input className="kw-input !py-2" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="staff-name" required />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Email</label>
          <input className="kw-input !py-2" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="staff-email" required />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Password</label>
          <input className="kw-input !py-2" type="text" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Mín. 6 caracteres"
            data-testid="staff-password" required />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Departamento</label>
          <input className="kw-input !py-2" placeholder="Marketing, Operações…"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            data-testid="staff-department" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Função</label>
          <input className="kw-input !py-2" placeholder="Gestor(a) de Plataformas, Recrutamento…"
            value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            data-testid="staff-job-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#737373] block mb-1">Telefone</label>
          <input className="kw-input !py-2" placeholder="+351 ..."
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="staff-phone" />
        </div>
        <button type="submit" className="kw-btn-primary inline-flex items-center gap-2" data-testid="staff-add">
          <UserPlus className="w-4 h-4" /> Adicionar
        </button>
      </form>

      <div className="kw-card p-5">
        <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
          <Briefcase className="w-5 h-5 text-[#E50000]" />
          <div className="kw-overline">Lista de Staff · {users.length}</div>
        </div>
        {users.length === 0 ? (
          <p className="text-sm text-[#737373] italic">Ainda não foi adicionado nenhum membro de staff.</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => {
              const p = pending[u.id] || {};
              const dirty = Object.keys(p).length > 0;
              return (
              <div key={u.id} className="border border-[#222] p-3 flex flex-wrap items-center gap-3" data-testid={`staff-row-${u.id}`}>
                <PhotoUpload userId={u.id} photoUrl={u.photo_url} name={u.name} size="sm" onChange={refresh} />
                <div className="min-w-[180px] max-w-[220px]">
                  <input
                    className="kw-input !py-1.5 !text-sm font-display font-black uppercase tracking-tight"
                    value={p.name ?? u.name}
                    onChange={(e) => setField(u.id, "name", e.target.value)}
                    data-testid={`staff-edit-name-${u.id}`} />
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{u.email}</div>
                </div>
                <input
                  className="kw-input !py-1.5 !text-xs !w-40"
                  placeholder="Departamento"
                  value={p.department ?? (u.department || "")}
                  onChange={(e) => setField(u.id, "department", e.target.value)}
                  data-testid={`staff-edit-department-${u.id}`} />
                <input
                  className="kw-input !py-1.5 !text-xs !w-48"
                  placeholder="Função (ex.: Gestor Plataformas)"
                  value={p.job_title ?? (u.job_title || "")}
                  onChange={(e) => setField(u.id, "job_title", e.target.value)}
                  data-testid={`staff-edit-job-title-${u.id}`} />
                <input
                  className="kw-input !py-1.5 !text-xs !w-40"
                  placeholder="+351 ..."
                  value={p.phone ?? (u.phone || "")}
                  onChange={(e) => setField(u.id, "phone", e.target.value)}
                  data-testid={`staff-edit-phone-${u.id}`} />
                <select
                  className="kw-input !py-1.5 !text-xs !w-28"
                  value={p.coach_mbti ?? (u.coach_mbti || "")}
                  onChange={(e) => setField(u.id, "coach_mbti", e.target.value || null)}
                  data-testid={`staff-edit-mbti-${u.id}`}>
                  <option value="">MBTI…</option>
                  {ALL_MBTI.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] cursor-pointer select-none" title="Permite a este staff atribuir plataformas (Idealista, Confidencial, Fizbot...) aos consultores">
                  <input
                    type="checkbox"
                    className="accent-[#E50000]"
                    checked={Boolean(p.is_platforms_manager ?? u.is_platforms_manager)}
                    onChange={(e) => setField(u.id, "is_platforms_manager", e.target.checked)}
                    data-testid={`staff-edit-platforms-mgr-${u.id}`} />
                  Plataformas
                </label>
                {u.club && (() => {
                  const ci = clubInfo(u.club);
                  return (
                    <span
                      className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                      style={{ color: ci?.color, borderColor: ci?.border, background: ci?.bg }}>
                      {u.club}
                    </span>
                  );
                })()}
                <button onClick={() => save(u.id)} disabled={!dirty}
                  className="kw-btn-primary text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  data-testid={`staff-save-${u.id}`}>
                  <Save className="w-3 h-3" /> Guardar
                </button>
                <button onClick={() => resetPassword(u.id, u.name)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white"
                  data-testid={`staff-reset-pwd-${u.id}`}>
                  Reset password
                </button>
                <button onClick={() => remove(u.id, u.name)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#737373] hover:text-[#E50000] flex items-center gap-1"
                  data-testid={`staff-remove-${u.id}`}>
                  <Trash2 className="w-3 h-3" /> Remover
                </button>
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
}
